import React, { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import AppLayout from "./layout/AppLayout";
import DashboardPage from "./pages/DashboardPage";
import DocumentsPage from "./pages/DocumentsPage";
import DocumentDetailsPage from "./pages/DocumentDetailsPage";
import RequireCompany from "./guards/RequireCompany";
import RequireHasCompanies from "./guards/RequireHasCompanies";
import LoginPage from "./pages/LoginPage";
import PaymentsPage from "./pages/PaymentsPage";
import VendorsPage from "./pages/VendorsPage";
import VendorDetailsPage from "./pages/VendorDetailsPage";
import AdditionalDocumentsPage from "./pages/AdditionalDocumentsPage";
import NotificationSettingsPage from "./pages/NotificationSettingsPage";
import AuthSuccessPage from "./pages/AuthSuccessPage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import RequireAdmin from "./guards/RequireAdmin";
import { ThemeProvider } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import theme from "./theme/theme";

const API_URL = process.env.REACT_APP_API_URL;

function AppContent() {
  const { i18n } = useTranslation();
  const appTheme = React.useMemo(() => theme(i18n.language), [i18n.language]);
  const currentPath = window.location.pathname;

  const { selectedCompany, authenticated, loading, refreshSession } = useAuth();

  const [companies, setCompanies] = useState<any[] | undefined>(undefined);

  const loadCompanies = useCallback(async () => {
    const res = await fetch(`${API_URL}/bc/companies`, {
      credentials: "include",
    });

    if (!res.ok) {
      setCompanies([]);
      return;
    }

    const data = await res.json();
    setCompanies(data.value || []);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authSuccess = params.get("auth");

    if (authSuccess === "success") {
      refreshSession().finally(() => {
        const cleanUrl = `${window.location.origin}${window.location.pathname}`;
        window.history.replaceState({}, document.title, cleanUrl);
      });
    }
  }, [refreshSession]);

  useEffect(() => {
    if (authenticated) {
      loadCompanies();
    } else {
      setCompanies(undefined);
    }
  }, [authenticated, loadCompanies]);

  const login = () => {
    window.location.href = `${API_URL}/auth/login`;
  };

  const logout = () => {
    sessionStorage.removeItem("selectedCompany");
    setCompanies(undefined);
    window.location.href = `${API_URL}/auth/logout`;
  };

  if (loading) {
    return null;
  }

  if (currentPath === "/auth-success") {
    return <AuthSuccessPage />;
  }

  if (!authenticated) {
    return <LoginPage onLogin={login} />;
  }

  return (
    <ThemeProvider theme={appTheme}>
      <Routes>
        <Route path="/auth-success" element={<AuthSuccessPage />} />
        <Route
          element={
            <AppLayout
              companies={companies || []}
              onLogout={logout}
              onCompaniesRefresh={loadCompanies}
            />
          }
        >
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <AdminUsersPage />
              </RequireAdmin>
            }
          />

          <Route
            element={
              <RequireHasCompanies companies={companies}>
                <RequireCompany selectedCompany={selectedCompany} />
              </RequireHasCompanies>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />

            <Route
              path="/documents"
              element={
                <DocumentsPage
                  companyId={selectedCompany}
                  viewMode="all"
                />
              }
            />

            <Route
              path="/documents/pending"
              element={
                <DocumentsPage
                  companyId={selectedCompany}
                  viewMode="pending"
                />
              }
            />

            <Route
              path="/documents/approved"
              element={
                <DocumentsPage
                  companyId={selectedCompany}
                  viewMode="approved"
                />
              }
            />

            <Route
              path="/documents/rejected"
              element={
                <DocumentsPage
                  companyId={selectedCompany}
                  viewMode="rejected"
                />
              }
            />

            <Route
              path="/payments"
              element={<PaymentsPage companyId={selectedCompany} />}
            />

            <Route
              path="/vendors"
              element={<VendorsPage companyId={selectedCompany} />}
            />

            <Route
              path="/vendors/:id"
              element={<VendorDetailsPage companyId={selectedCompany} />}
            />

            <Route
              path="/document/:id"
              element={<DocumentDetailsPage />}
            />

            <Route
              path="/additional-documents"
              element={
                <AdditionalDocumentsPage companyId={selectedCompany} />
              }
            />

            <Route
              path="/settings/notifications"
              element={<NotificationSettingsPage />}
            />
          </Route>

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}